import * as React from "react";
import { Form, Row, Col, Select, DatePicker, Tree, TreeDataNode } from "antd";
import type { Dayjs } from "dayjs";
import Ring from "@/components/ui/Ring";
import { log } from "@/utils/logger";
import type { DataSourceOption } from "@/state/actuals/commonEquityStore";

interface DataSourceFormProps {
  dataSource: DataSourceOption;
  onDataSourceChange: (value: DataSourceOption) => void;
  asOfDate: Dayjs | null;
  onAsOfDateChange: (value: Dayjs | null) => void;
}

const DataSourceForm: React.FC<DataSourceFormProps> = ({
  dataSource,
  onDataSourceChange,
  asOfDate,
  onAsOfDateChange,
}) => {
  const [selectedUnnKey, setSelectedUnnKey] = React.useState<string | null>(null);

  const treeData: TreeDataNode[] = [
    {
      title: "Public Information",
      key: "PropData_Public Information",
      children: [
        {
          title: "UNN: Tech IBD Global",
          key: "PropData_Public Information_Tech IBD Global",
          isLeaf: true,
        },
      ],
    },
    {
      title: "Project Rocket_2024",
      key: "PropData_Project Rocket_2024",
      children: [
        {
          title: "UNN: Company Data",
          key: "PropData_Project Rocket_2024_Company Data",
          isLeaf: true,
        },
      ],
    },
  ];

  React.useEffect(() => {
    // If dataSource is a PropData key, extract and set the selected key
    if (dataSource.startsWith("PropData_")) {
      setSelectedUnnKey(dataSource);
    } else {
      setSelectedUnnKey(null);
    }
  }, [dataSource]);

  const handleTreeSelect = (selectedKeys: React.Key[], info: any) => {
    if (selectedKeys.length === 0) {
      setSelectedUnnKey((prev) => prev);
      return;
    }

    if (selectedKeys.length > 0 && info.node.isLeaf) {
      const selected = selectedKeys[0].toString();
      setSelectedUnnKey(selected);
      // Update the dataSource with the full PropData key
      onDataSourceChange(selected);
    } else {
      setSelectedUnnKey((prev) => prev);
    }
  };

  return (
    <div>
      <Form.Item name="dataSource" className="!mb-2">
        <Row align="middle" gutter={16}>
          {/* Data Source */}
          <Col span={12}>
            <Row align="middle">
              <Col span={8} style={{ display: "flex", justifyContent: "flex-start" }}>
                <label htmlFor="dataSource" className="">
                  Data Source:
                </label>
              </Col>
              <Col span={16} style={{ display: "flex", justifyContent: "flex-start" }}>
                <Select<string>
                  id="dataSource"
                  value={dataSource.startsWith("PropData_") ? "Proprietary Data" : dataSource}
                  onChange={(value) => {
                    if (value === "AlphaScript") {
                      onDataSourceChange("AlphaScript");
                      setSelectedUnnKey(null);
                    } else if (value === "Proprietary Data") {
                      // If there's already a selected UNN, keep it
                      if (selectedUnnKey) {
                        onDataSourceChange(selectedUnnKey);
                      } else {
                        // Auto-select the first UNN by default
                        const firstUnnKey = treeData[0]?.children?.[0]?.key as string;
                        if (firstUnnKey) {
                          setSelectedUnnKey(firstUnnKey);
                          onDataSourceChange(firstUnnKey);
                        }
                      }
                    }
                  }}
                  style={{ width: "70%" }}
                  options={[
                    { label: "AlphaScript", value: "AlphaScript" },
                    { label: "Proprietary Data", value: "Proprietary Data" },
                  ]}
                />
              </Col>
            </Row>
          </Col>

          {/* As of Date */}
          <Col span={12}>
            <Form.Item name="asOfDate" className="!mb-0">
              <Row align="middle">
                <Col span={8} style={{ display: "flex", justifyContent: "flex-start" }}>
                  <label htmlFor="asOfDate" className="">
                    As of Date:
                  </label>
                </Col>
                <Col span={16} style={{ display: "flex", justifyContent: "flex-start" }}>
                  <DatePicker
                    id="asOfDate"
                    style={{ width: "70%" }}
                    value={asOfDate}
                    onChange={onAsOfDateChange}
                  />
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>

      {/* Data Type Tree View: only for Proprietary Data */}
      {(dataSource === "Proprietary Data" || dataSource.startsWith("PropData_")) && (
        <div className="flex gap-2 flex-col items-start mt-4">
          <label className="whitespace-nowrap text-start font-bold">
            Select Access Group and Unique Upload Number (UNN)
          </label>
          <Ring className="w-3/5 py-4 px-2">
            <Tree
              defaultExpandAll
              selectedKeys={selectedUnnKey ? [selectedUnnKey] : []}
              onSelect={handleTreeSelect}
              treeData={treeData}
              showLine={{ showLeafIcon: false }}
            />
          </Ring>
          <div className="w-full text-left text-xs text-[var(--color-as-dark-gray)] mt-1">
            Don&apos;t see your Access Group? Click{" "}
            <button
              type="button"
              className="text-[var(--color-as-dark-blue)] underline font-semibold hover:text-[var(--color-as-orange-primary)] hover:cursor-pointer"
              onClick={() => {
                log.info("Access Groups link clicked");
              }}
            >
              here
            </button>{" "}
            to create and manage Access Groups.
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourceForm;
